import { Fragment, useMemo } from 'react'
import { periodenkuerzel } from './api'
import { beschriftung, katalog, t } from './sprache'
import { ohneWert } from './Ohnewert'
import { eigeneZellen, kreuzzelle, moeglicheAchsen, vorgabeFuer, wertAuf } from './Aufriss'
import type { Zelle } from './Aufriss'
import { Aufrisszeilen, ebenenschluessel, menueort, zellkontext, zusatzachsen } from './Aufrisszeilen'
import type { Achsenquelle, Menueruf } from './Aufrisszeilen'
import {
  berichtsmenue,
  einzug,
  istAufreissbar,
  zeilenachsen,
  istLeereBerichtszeile,
  istTrefferzelle,
  positionAus,
  trefferklasse,
  rangklasse,
  Wert,
  Zeilenbeschriftung,
  Zeilenkopf,
  zeilenname,
  Zellen,
} from './Bericht'
import type { Aufrissbedienung, Nachweisruf, Trefferzustand } from './Bericht'
import { alleNull, ohneLeereZeilen, unterdrueckt, useAnzeigeoptionen } from './Anzeigeoptionen'
import { useGliederungsbild } from './Gliederung'
import { Spaltenleiste, useSpaltenbreiten } from './Spaltenbreiten'
import { periodenspalte, zeilenId } from './Suche'
import { Zeilenmenue } from './Zeilenmenue'
import { spaltenfolge, verdichte, zwischenkopf, zwischenspalten } from './Zwischensummen'
import type { Eigenschaftsziel } from './Eigenschaftsfenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'
import type {
  Aufrisszelle,
  Bericht as Berichtssatz,
  Periodenaufriss,
  Periodenbericht,
  Periodenspalte,
  Zusatzzelle,
} from './typen'

/**
 * Die Bilanz und die GuV in den Bildern, deren Spalten eine Achse tragen —
 * und im Periodenbild.
 *
 * Alle zeigen dieselben Zeilen und **denselben Aufriss** wie der
 * Konsolidierungsbericht — nur die Spalten sind andere Fragen an dieselben
 * Zahlen. Genau deshalb liegt der Aufriss in eigenen Bausteinen: Er betrifft
 * die Zeilenachse, und die Spalten wissen nichts davon.
 */

/**
 * Die drei Bilder, die auf demselben Bericht stehen und deren Spalten
 * Summe · Konsolidierung · Konzern sind — mit oder ohne Achse davor.
 *
 * | Bild | Spalten davor |
 * |---|---|
 * | *Konzernstruktur* | je Gesellschaft ihre Handelsbilanz II |
 * | *Verkürzter Konzernaufriss* | keine — nur Summe, Konsolidierung, Konzern |
 * | *Partner* | je Partner die Handelsbilanz II gegen ihn, dann „Default" |
 */
export type Strukturbild = 'struktur' | 'kurz' | 'partner'

/**
 * Spalten nach einer Achse, dann **Summe**, dann die **Konsolidierung in
 * einer Spalte**, dann der **Konzernwert**.
 *
 * **In den Achsenspalten steht die Handelsbilanz II** — was die Gesellschaft
 * selbst ausweist, vor jeder Eliminierung. Erst so ergibt die Zeile das
 * Schema, das ein Fachanwender im Kopf hat: Summe der Einzelbilanzen plus
 * Konsolidierung gleich Konzern. Im Bild *Partner* ist es dieselbe Zahl,
 * anders geschnitten: die Handelsbilanz II je Partner, gegen den sie steht.
 *
 * **Mit gewählter Gesellschaft** zeigt das Partnerbild nur sie: ihre
 * Handelsbilanz II je Partner, und als Summe ihre ganze — mit ihrem Namen
 * in der Überschrift. Konsolidierung und Konzernwert entfallen dann, weil
 * sie keine Größen einer einzelnen Gesellschaft sind.
 */
export function BerichtNachStruktur({
  bericht,
  bild,
  gesellschaft = null,
  partnerzellen = [],
  aufriss,
  quelle,
  treffer,
  aufNachweis,
  aufEigenschaften,
  aufReihenfolge,
  aufBuchen,
  aufAnlegen,
  offeneStufen,
  aufStufe,
}: {
  bericht: Berichtssatz
  bild: Strukturbild
  /** Im Partnerbild: die gewählte Gesellschaft — `null` heißt der ganze Kreis. */
  gesellschaft?: string | null
  /** Im Partnerbild: der Würfel je Partner, auf Abruf geholt (7.3 Abschnitt 2f). */
  partnerzellen?: Zusatzzelle[]
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Aufrisszelle>
  treffer?: Trefferzustand
  /** Doppelklick auf eine Zahl öffnet ihren Wertenachweis (R204). */
  aufNachweis?: Nachweisruf
  /** Rechtsklick → Buchen (R385). */
  aufBuchen?: Nachweisruf
  /** Rechtsklick → Anlegen: die fünfte Gruppe (R494, R500 — ein Schalter je Bericht). */
  aufAnlegen?: (art: 'konto' | 'position', position: string, positionsname: string) => void
  /**
   * Welche Teilkonzerne aufgeklappt sind (R407).
   *
   * **Der Zustand kommt von außen, weil er in der Adresse steht** (R156, wie
   * R402): Ein weitergegebener Verweis zeigt dieselbe Tabelle wie beim
   * Absender.
   */
  offeneStufen?: Set<string>
  aufStufe?: (schluessel: string) => void
  /** Rechtsklick → Eigenschaften (R858). */
  aufEigenschaften?: (ziel: Eigenschaftsziel) => void
  /** Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341). */
  aufReihenfolge?: (ziel: Reihenfolgeziel) => void
}) {
  const { menue, setMenue } = aufriss
  const nach = katalog()
  const offen = offeneStufen ?? new Set<string>()
  const stufen = bericht.stufen ?? []
  const stufenstelle = new Map(stufen.map((s, i) => [s.schluessel, i]))

  // Der Name des Berichts steht in der Tabelle und nicht darüber (R411).
  // Abgeleitet und nicht durchgereicht: Der Bericht weiß, was er ist.
  const berichtsname = t(
    bericht.art === 'guv' ? 'bereich.berichte.guv' : 'bereich.berichte.bilanz',
  )

  const aufMenue: Menueruf = (x, y, position, eltern, zelle, kontext) =>
    setMenue({ x, y, position, eltern, zelle, kontext })

  // Die Spalten vor der Summe: Gesellschaften und Teilkonzerne, Partner —
  // oder keine.
  const achse = bild === 'struktur' ? 'einheiten' : bild === 'partner' ? 'partner' : null
  const alleSpalten: Strukturspalte[] =
    bild === 'struktur'
      ? /*
           **Eine gewählte Gesellschaft zeigt ihre Spalte und keine andere**
           (2.3 Abschnitt 10.3, R714): Wer oben *Holding AG* als
           Berichtsbezug gewählt hat, fragt nach ihrer Handelsbilanz II — was
           sie selbst beisteuert, vor jeder Eliminierung.

           **Dieselben Zahlen, anders geschnitten.** Gerechnet wird weiter der
           ganze Kreis; hier fällt nur weg, was nicht zu ihr gehört.
           Konsolidierung und Konzernwert entfallen ohnehin (`mitKonzern`) —
           sie sind keine Größen einer einzelnen Gesellschaft.
        */
        strukturspalten(bericht, offen).filter(
          (s) => gesellschaft === null || s.kennung === gesellschaft,
        )
      : bild === 'partner'
        ? partnerIn(partnerzellen).map((w) => ({ art: 'achse' as const, kennung: w }))
        : []

  // **Leere Posten stehen da, bis einer sie wegnimmt** (7.3 Abschnitt 11).
  const optionen = useAnzeigeoptionen()
  const vollstaendig = unterdrueckt(optionen, 'positionen')
    ? ohneLeereZeilen(bericht.zeilen, istLeereBerichtszeile)
    : bericht.zeilen

  // **Und was einer zusammengeschoben hat, bleibt zusammengeschoben**
  // (7.3 Abschnitt 13, R444). Nach der Nullzeilenunterdrueckung gerechnet:
  // Der Pfeil gehoert an das, was die Tabelle wirklich zeigt.
  const gliederung = useGliederungsbild(vollstaendig)
  const zeilen = gliederung.zeilen

  /*
    **Was fuer die Zeilen gilt, gilt fuer die Spalten** (R421): Wer die leeren
    Gesellschaften nicht sehen will, meint sie auch dort, wo sie quer stehen.

    Nur die ACHSENSPALTEN werden geprueft. Die drei Spalten eines
    aufgeklappten Teilkonzerns gehoeren zu seinem Block und nicht zur
    Gesellschaftsachse -- eine davon wegzunehmen liesse einen Block mit zwei
    Spalten zurueck, und die Zerlegung *Summe + Konsolidierung = Gesamt* waere
    keine mehr.
  */
  const spalten =
    achse && unterdrueckt(optionen, achse)
      ? alleSpalten.filter(
          (s) =>
            s.art !== 'achse' ||
            // Gegen die ganze Gliederung geprueft, nicht gegen die
            // zusammengeschobene: Eine Spalte verschwindet, weil in ihr nichts
            // steht (R421) -- und nicht, weil einer eine Zeile zugeklappt hat.
            vollstaendig.some(
              (z) =>
                saeulenwert(s, z.position, { einheit: null, konto: null, ...z }, z.beitraege) !== 0,
            ),
        )
      : alleSpalten
  /*
    **Ohne Lauf gibt es keine Konsolidierung** (R753, R759) — und damit auch
    keine Spalte dafür. Was hier steht, ist addiert und nicht konsolidiert;
    eine Spalte *Konsolidierung* neben lauter Nullen wäre eine Behauptung über
    eine Rechnung, die gar nicht stattgefunden hat.

    Dieselbe Überlegung wie bei einer einzelnen Gesellschaft: Konsolidierung
    und Konzernwert sind keine Größen, die ihr gehören.
  */
  const mitKonzern = gesellschaft === null && bericht.herkunft !== 'geliefert'

  /*
    **Eine Summe über eine einzige Spalte ist keine** (R761).

    Bei einer einzelnen Gesellschaft stand die Kopfzeile bis zum 2026-09-25
    zweimal da — *Tochter 1 GmbH · Tochter 1 GmbH* —, und darunter zweimal
    dieselbe Zahl. Die zweite Spalte beantwortete keine Frage, die die erste
    nicht schon beantwortet hat.

    **Mit Konsolidierung bleibt sie**, denn dann sagt sie etwas: die Summe der
    Gesellschaften *vor* der Konsolidierung, links von deren Spalte. Und wo gar
    keine Achsenspalte steht, ist sie die einzige Zahl, die es gibt.
  */
  const mitSumme = spalten.length !== 1 || mitKonzern

  /*
    **Die Saldowechselspalte erscheint nur, wenn sie einen Betrag trägt**
    (2.7 Abschnitt 3.7, R535) — wie eine Gesellschaft ohne einen einzigen
    Betrag keine Spalte bekommt. Im Regelfall ändert sich am Bericht nichts.

    Sie steht **zwischen den Gesellschaftsspalten und der Summe**, weil genau
    dort ihre Rechnung sichtbar wird: Was die Spalten links zusammen sagen,
    plus dieser Betrag, ist die Summe rechts. Sie ist **keine
    Konsolidierungsspalte** — dort steht, *wie* aus Einzelabschlüssen ein
    Konzern wird, hier, *wo* das Ergebnis ausgewiesen ist.
  */
  const mitSaldowechsel = zeilen.some((z) => (z.saldowechsel ?? 0) !== 0)

  // Je Bild eine Breite (7.3, Abschnitt 10). Die Spaltenzahl schliesst die
  // Positionsspalte ein und wechselt mit jedem aufgeklappten Teilkonzern.
  const { breiten, rahmen } = useSpaltenbreiten(`${bericht.art}:${bild}`)
  const spaltenzahl = 1 + spalten.length + (mitSumme ? 1 : 0) + (mitSaldowechsel ? 1 : 0) + (mitKonzern ? 2 : 0)

  // Zwei Kopfzeilen gibt es nur, wenn ein Teilkonzern offen steht. Sonst wäre
  // die zweite leer — und eine leere Zeile ist kein Aufbau, sondern ein Rand.
  const zweizeilig = alleSpalten.some((s) => s.block)

  function kopf(sp: Strukturspalte): React.ReactNode {
    if (sp.art === 'stufensumme') {
      return t('spalte.tk.summe')
    }

    if (sp.art === 'stufenkonso') {
      return t('spalte.tk.konsolidierung')
    }

    const stufe = stufen.find((s) => s.schluessel === sp.kennung)

    // **Aufgeklappt trägt die Überschrift den Schalter, nicht die letzte
    // Spalte.** Sie steht über den Töchtern auf der Höhe der Holding — dort
    // sucht man ihn, und dort ist er.
    if (sp.art === 'stufengesamt') {
      return t('spalte.tk.gesamt')
    }

    // **Zugeklappt ist die Spalte selbst der Schalter**: Sie ist das Einzige,
    // was von dem Teilkonzern da ist (R407).
    if (sp.art === 'stufe' && stufe) {
      return (
        <button
          type="button"
          className="spaltenklapper"
          aria-expanded={false}
          title={t('spalte.tk.aufklappen', { name: beschriftung(stufe.namen) || stufe.code })}
          onClick={() => aufStufe?.(stufe.schluessel)}
        >
          {t('spalte.tk.marke', { code: stufe.code })} <span aria-hidden="true">▸</span>
          <span className="spaltenname">{beschriftung(stufe.namen) || stufe.code}</span>
        </button>
      )
    }

    if (sp.kennung === null) {
      return ohneWert('weitere')
    }

    // Im Partnerbild beschriftet der Partnercode, im Strukturbild die
    // Gesellschaft — und wo kein Code gepflegt ist, weiterhin die Gesellschaft (R490).
    const e =
      achse === 'partner'
        ? (nach.partner.get(sp.kennung) ?? nach.einheiten.get(sp.kennung))
        : nach.einheiten.get(sp.kennung)
    return (
      <>
        {e?.code ?? sp.kennung}
        {e && beschriftung(e.namen) && <span className="spaltenname">{beschriftung(e.namen)}</span>}
      </>
    )
  }

  const summenkopf = (() => {
    if (gesellschaft === null) {
      return t('spalte.summe')
    }

    const e = nach.einheiten.get(gesellschaft)
    return (
      <>
        {e?.code ?? gesellschaft}
        {e && beschriftung(e.namen) && <span className="spaltenname">{beschriftung(e.namen)}</span>}
      </>
    )
  })()

  /** Die Handelsbilanz II einer Zelle — auf die gewählte Gesellschaft eingegrenzt, wo eine gewählt ist. */
  function hb2Von(z: Aufrisszelle | Zelle, vorrat: Aufrisszelle[]): number {
    if (gesellschaft === null) {
      return (z as Aufrisszelle).hb2 ?? 0
    }

    return kreuzzelle(vorrat, z, 'einheiten', gesellschaft)?.hb2 ?? 0
  }

  /**
   * Was eine Zelle in einer Achsenspalte trägt.
   *
   * Für die Gesellschaften kommt die Kreuzzelle aus dem Würfel des Berichts
   * (R212). Für die Partner aus dem Abruf: je Position, Gesellschaft und
   * Konto der Wert gegen diesen Partner. Eine Zeile, die selbst schon einen
   * Partner nennt, steht in ihrer eigenen Spalte und in keiner anderen.
   */
  /**
   * Alles, was in dieser Zelle innerhalb von Teilkonzernen eliminiert wird.
   *
   * **Unabhängig davon, was gerade aufgeklappt ist** (R410): Die Summenspalte
   * summiert die Stufe, und die gibt es, ob man sie sieht oder nicht.
   */
  function stufensumme(z: Zelle & Partial<Aufrisszelle>): number {
    return (z.stufenkonso ?? []).reduce((s, w) => s + w, 0)
  }

  /** Was in einem Teilkonzern dieser Zelle eliminiert wird (R405). */
  function stufenkonso(z: Zelle & Partial<Aufrisszelle>, stufe: string): number {
    const i = stufenstelle.get(stufe)
    return i === undefined ? 0 : (z.stufenkonso?.[i] ?? 0)
  }

  /**
   * Der Wert einer Spalte des Strukturbilds.
   *
   * **Ein Teilkonzern ist die Summe seiner Gesellschaften plus das, was in ihm
   * eliminiert wird** (R405). Zugeklappt steht dort diese eine Zahl,
   * aufgeklappt dieselbe in drei Spalten zerlegt — die Zerlegung ändert keinen
   * Betrag, nur ihre Zahl.
   */
  function saeulenwert(
    sp: Strukturspalte,
    position: string,
    z: Zelle & Partial<Aufrisszelle>,
    vorrat: Aufrisszelle[],
  ): number {
    if (sp.art === 'achse') {
      return spaltenwert(position, z, vorrat, sp.kennung)
    }

    const stufe = stufen.find((s) => s.schluessel === sp.kennung)
    if (!stufe) {
      return 0
    }

    const summe = stufe.mitglieder.reduce((s, e) => s + spaltenwert(position, z, vorrat, e), 0)
    const konso = stufenkonso(z, stufe.schluessel)

    return sp.art === 'stufensumme' ? summe : sp.art === 'stufenkonso' ? konso : summe + konso
  }

  function spaltenwert(
    position: string,
    z: Zelle,
    vorrat: Aufrisszelle[],
    kennung: string | null,
  ): number {
    if (achse === 'einheiten') {
      return kreuzzelle(vorrat, z, 'einheiten', kennung)?.hb2 ?? 0
    }

    const eigen = wertAuf(z, 'partner')
    if (eigen !== undefined) {
      return eigen === kennung ? hb2Von(z, vorrat) : 0
    }

    // Eine Zelle, die auf einer anderen Zusatzachse steht, hat im
    // Partnerwürfel keine Kreuzzelle — dort steht dann nichts, statt einer
    // Zahl, die nicht stimmt.
    const andere = Object.keys(z.weitere ?? {})
    if (andere.length > 0) {
      return 0
    }

    const einheit = z.einheit ?? gesellschaft
    return (
      partnerzellen.find(
        (p) =>
          p.position === position && p.wert === kennung && p.einheit === einheit && p.konto === z.konto,
      )?.hb2 ?? 0
    )
  }

  function spaltenkennung(kennung: string | null): string {
    return `${achse}:${kennung ?? '∅'}`
  }


  return (
    <div className="tabellenrahmen" ref={rahmen}>
      <table className="positionstabelle">
        <Spaltenleiste breiten={breiten} spalten={spaltenzahl} />
        <thead>
          {/*
            **Die Überschrift des Teilkonzerns steht auf der Höhe der
            Holding** (R408): Sie ist ihresgleichen — eine Gesellschaft der
            ersten Ebene —, und ihre Töchter stehen eine Zeile darunter. So
            liest sich der Kopf wie der Konzern und nicht wie eine Beschriftung
            über einer Beschriftung.

            **Sie trägt auch den Schalter zum Zuklappen.** Wer aufgerissen hat,
            sucht ihn dort, wo der Teilkonzern steht — nicht an dessen letzter
            Spalte.
          */}
          <tr>
            {/*
              **Der Berichtsname steht in der Tabelle**, wo sonst „Position"
              stand (R411). Das Wort erklärt sich am Inhalt der Spalte von
              selbst, und die Überschrift darüber kostete eine Zeile
              Bildschirm, die die Tabelle besser gebraucht.
            */}
            <th rowSpan={zweizeilig ? 2 : 1} className="positionsspalte">
              {berichtsname}
            </th>

            {blockleiste(spalten).map((b, i) =>
              b.stufe === null ? (
                b.spalten.map((s) => (
                  <th
                    key={saeulenkennung(s)}
                    rowSpan={zweizeilig ? 2 : 1}
                    className={saeulenklasse(s, 'zahl')}
                  >
                    {kopf(s)}
                  </th>
                ))
              ) : (
                <th key={`band${i}`} colSpan={b.spalten.length} className="stufenband">
                  <button
                    type="button"
                    className="spaltenklapper"
                    aria-expanded
                    title={t('spalte.tk.zuklappen', {
                      name: beschriftung(stufen.find((s) => s.schluessel === b.stufe)?.namen) || b.stufe,
                    })}
                    onClick={() => aufStufe?.(b.stufe!)}
                  >
                    <span aria-hidden="true">▾</span>{' '}
                    {t('spalte.tk.band', {
                      name:
                        beschriftung(stufen.find((s) => s.schluessel === b.stufe)?.namen) ||
                        (b.stufe ?? ''),
                    })}
                  </button>
                </th>
              ),
            )}

            {mitSaldowechsel && (
              <th rowSpan={zweizeilig ? 2 : 1} className="zahl saldowechsel">
                {t('spalte.saldowechsel')}
              </th>
            )}
            {mitSumme && (
              <th rowSpan={zweizeilig ? 2 : 1} className="zahl spaltensumme">
                {summenkopf}
              </th>
            )}
            {mitKonzern && (
              <th rowSpan={zweizeilig ? 2 : 1} className="zahl">
                {t('spalte.konsolidierung')}
              </th>
            )}
            {mitKonzern && (
              <th rowSpan={zweizeilig ? 2 : 1} className="zahl konzern">
                {t('spalte.konzern')}
              </th>
            )}
          </tr>

          {zweizeilig && (
            <tr className="blockleiste">
              {spalten
                .filter((s) => s.block)
                .map((s) => (
                  <th key={saeulenkennung(s)} className={saeulenklasse(s, 'zahl')}>
                    {kopf(s)}
                  </th>
                ))}
            </tr>
          )}
        </thead>
        <tbody>
          {zeilen.map((zeile) => {
            const offen = aufriss.zustand.get(ebenenschluessel(zeile.position, []))
            const vorgabe = vorgabeFuer(zeile.beitraege)
            const moeglich = zeilenachsen(zeile, moeglicheAchsen(zeile.beitraege, [], zusatzachsen(quelle)))
            const aufreissbar = istAufreissbar(zeile, moeglich)
            const id = zeilenId(zeile.position, [])
            const leer: Zelle = { einheit: null, konto: null }
            const name = zeilenname(zeile)

            /** Die Wertzellen einer Zeile oder Aufrisszelle dieses Bilds. */
            const werte = (
              z: Zelle & Partial<Aufrisszelle>,
              kennung: string,
              vorrat: Aufrisszelle[],
              /*
                **Der Deltabetrag gehört der Positionszeile, nicht dem
                Aufriss.** Eine Gesellschaftszeile darunter steht in ihrer
                eigenen Spalte und hat nichts auszugleichen — dort stünde er
                ein zweites Mal.
              */
              delta = 0,
            ) => (
              <Zellen ruf={aufNachweis ?? null} position={zeile.position} positionsname={name} zelle={z}>
                {spalten.map((s) => (
                  // In der Achsenspalte steht die Handelsbilanz II — der
                  // Nachweis dazu ist der dieser Gesellschaft oder dieses
                  // Partners, nicht der der ganzen Zeile. Deshalb reist die
                  // Zelle mit (R205).
                  //
                  // **Eine Teilkonzernspalte hat keinen eigenen Nachweis:** Sie
                  // ist eine Summe über mehrere Gesellschaften, und ein
                  // Nachweis darauf müsste mehrere Herleitungen aneinanderhängen.
                  // Wer wissen will, woraus sie besteht, klappt sie auf.
                  <Wert
                    key={saeulenkennung(s)}
                    wert={saeulenwert(s, zeile.position, z, vorrat)}
                    klasse={saeulenklasse(s)}
                    spalte={s.art === 'achse' ? 'hb2' : undefined}
                    zelle={
                      s.art !== 'achse'
                        ? undefined
                        : achse === 'einheiten'
                          ? { einheit: s.kennung, konto: z.konto, weitere: z.weitere }
                          : {
                              einheit: z.einheit ?? gesellschaft,
                              konto: z.konto,
                              weitere: { ...z.weitere, partner: s.kennung },
                            }
                    }
                    fund={istTrefferzelle(treffer, kennung, spaltenkennung(s.kennung))}
                  />
                ))}
                {/*
                  Die Summenspalte trägt den Wert der Zeile, gleich auf
                  welcher Achse sie steht: Sie ist die Summe der Spalten links
                  davon, und die ist bei einer Gesellschaftszeile die eine
                  gefüllte Spalte.
                */}
                {/*
                  **Die Summenspalte summiert die STUFE** (R409): die
                  Einzelbilanzen und die konsolidierten Teilkonzerne. Was in
                  einem Teilkonzern schon eliminiert ist, steht damit hier —
                  und rechts nur noch der Rest. Kein Doppelcount: Jede
                  Eliminierung wird genau einmal gezählt.

                  Aufklappen ändert an dieser Zahl nichts (R410). Sie hängt
                  daran, WAS es an Stufen gibt, nicht daran, was gerade offen
                  steht.
                */}
                {mitSaldowechsel && (
                  /*
                    **Kein eigener Nachweis.** Der Deltabetrag ist die Differenz
                    zweier Spalten und keine Spalte des Ergebnisses — dieselbe
                    Überlegung wie bei der Konsolidierungsspalte darunter. Wer
                    wissen will, warum eine Zahl woanders steht, öffnet den
                    Wertenachweis der Zeile (R536).
                  */
                  <Wert wert={delta} klasse="saldowechsel" />
                )}
                {mitSumme && (
                  <Wert
                    wert={hb2Von(z, vorrat) + stufensumme(z)}
                    klasse="spaltensumme"
                    spalte={stufensumme(z) === 0 ? 'hb2' : undefined}
                    zelle={gesellschaft === null ? undefined : { einheit: gesellschaft, konto: z.konto, weitere: z.weitere }}
                    fund={istTrefferzelle(treffer, kennung, 'summe')}
                  />
                )}
                {/*
                  **Die Konsolidierungsspalte trägt seit dem 2026-09-27 ihren
                  Nachweis** (R829). Bis dahin stand hier: *„Sie ist die
                  Differenz zweier Spalten, keine Spalte des Ergebnisses — ein
                  Nachweis darauf müßte zwei Herleitungen voneinander
                  abziehen."* Das stimmt und hindert nicht: Die Zeilen hinter
                  der Differenz sind genau die, die **nicht** in der
                  Handelsbilanz II stehen, und der Nachweis liest sie in einem
                  Zug — über alle Konsolidierungsstufen zugleich.

                  **Auftrag des Auftraggebers vom 2026-09-27:** *„Wenn ich also
                  auf die −61000 klicke, möchte ich in selbem Format wie bei
                  den Gesellschaften sehen, wann etwas warum passiert ist."*

                  **Nicht, solange ein Teilkonzernband offensteht:** Dann zeigt
                  die Zelle nur die oberste Stufe, und der Nachweis über alle
                  beantwortete eine andere Frage als die angeklickte. Dasselbe
                  Maß wie bei der Summenspalte darüber.
                */}
                {mitKonzern && (
                  <Wert
                    wert={(z.konzern ?? 0) - (z.hb2 ?? 0) - stufensumme(z)}
                    spalte={stufensumme(z) === 0 ? 'konsolidierung' : undefined}
                    fund={istTrefferzelle(treffer, kennung, 'konsolidierung')}
                  />
                )}
                {mitKonzern && (
                  <Wert
                    wert={z.konzern ?? 0}
                    klasse="konzern"
                    spalte="konzern"
                    fund={istTrefferzelle(treffer, kennung, 'konzern')}
                  />
                )}
              </Zellen>
            )

            return (
              <Fragment key={zeile.position}>
                <tr
                  id={id}
                  className={[
                    rangklasse(zeile),
                    aufreissbar ? 'aufklappbar' : '',
                    // Keine Dämpfung für eine Zeile ohne Betrag (R867, 7.3 Abschnitt 21):
                    // Das leere Wertfeld sagt es, und jede Zeile steht in der Farbe ihrer Ebene.
                    trefferklasse(treffer, id),
                  ]
                    .join(' ')
                    .trim()}
                  tabIndex={0}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    const ort = menueort(e, e.currentTarget)
                    aufMenue(ort.x, ort.y, zeile.position, [], leer, zellkontext(e))
                  }}
                >
                  <td style={{ paddingLeft: einzug(zeile.ebene) }}>
                    <Zeilenkopf
                      aufreissbar={aufreissbar}
                      achse={offen}
                      vorgabe={vorgabe}
                      angebot={quelle}
                      aufKlappen={() =>
                        aufriss.umschalten(zeile.position, [], offen ?? moeglich[0] ?? vorgabe)
                      }
                      aufMenue={(x, y) => aufMenue(x, y, zeile.position, [], leer, {})}
                      klappbar={gliederung.klappbar(zeile.position)}
                      zugeklappt={gliederung.zugeklappt(zeile.position)}
                      aufGliederung={() => gliederung.umschalten(zeile.position)}
                    >
                      <Zeilenbeschriftung zeile={zeile} />
                    </Zeilenkopf>
                  </td>
                  {werte(
                    // Die Zeile selbst als Zelle: die Summe über beide
                    // Grundachsen, mit den Werten der Zeile — und mit ihren
                    // Stufenbeträgen, ohne die die Summenspalte die Stufe
                    // nicht summieren könnte (R409).
                    {
                      einheit: null,
                      konto: null,
                      hb2: zeile.hb2,
                      konzern: zeile.konzern,
                      stufenkonso: zeile.stufenkonso,
                    },
                    id,
                    zeile.beitraege,
                    zeile.saldowechsel ?? 0,
                  )}
                </tr>

                {/*
                  **Nur unter einer Zeile, die aufgerissen werden darf** (R165, R432).
                  Pfeil und Menü führen nicht auf eine Summenzeile — aber der
                  Aufklappzustand kommt auch von woanders: aus einem anderen Bericht,
                  aus der Adresse, und seit heute aus einem Suchtreffer. Ohne diesen
                  Riegel zeichnet die Tabelle, was kein Weg ihr hätte sagen dürfen.
                */}
                {aufreissbar && (
                  <Aufrisszeilen
                    position={zeile.position}
                    summe={zeile.istSumme}
                    ebeneTiefe={zeile.ebene}
                    zellen={zeile.beitraege}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={spalten.length + (mitSaldowechsel ? 1 : 0) + (mitKonzern ? 4 : 2)}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    // **Auch eine Kontozeile verteilt sich auf die Spalten**
                    // (R180, R212). Der Würfel führt die Kreuzzelle Gesellschaft×Konto
                    // mit; sie hier zu übergehen hieße, die Zeile darunter anders
                    // zu lesen als die Zeile darüber.
                    werteSpalten={(z, kennung, pfad) =>
                      werte(z, kennung, aufriss.vorratFuer(quelle, zeile.beitraege, zeile.position, pfad))
                    }
                  />
                )}

                {/* Die eigenen Konten vor den Unterpositionen (7.3 Abschnitt 30, R1621). */}
                {!gliederung.zugeklappt(zeile.position) && eigeneZellen(zeile).length > 0 && (
                  <Aufrisszeilen
                    position={zeile.position}
                    fest="konten"
                    ebeneTiefe={zeile.ebene}
                    zellen={eigeneZellen(zeile)}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={spalten.length + (mitSaldowechsel ? 1 : 0) + (mitKonzern ? 4 : 2)}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    // Die Kreuzzellen je Gesellschaft kommen aus der ganzen
                    // Zeile, wie unter jedem Konto (R180, R212).
                    werteSpalten={(z, kennung, pfad) =>
                      werte(z, kennung, aufriss.vorratFuer(quelle, zeile.beitraege, zeile.position, pfad))
                    }
                  />
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={berichtsmenue({
            menue,
            aufEigenschaften,
            aufReihenfolge,
            bericht,
            aufriss,
            quelle,
            aufNachweis,
            aufAnlegen,
            aufBuchen,
            gliederung: gliederung.menue,
          })}
          aufSchliessen={() => setMenue(null)}
        />
      )}
    </div>
  )
}

/**
 * Spalten nach Perioden — mit Aufriss, ohne den Zeitverlauf zu verlieren.
 *
 * **Das ist der Fall, für den der Pfad-Aufriss gebaut wurde:** Die Vorräte
 * springen in einem Monat, ein Rechtsklick auf das Konto zeigt die
 * Gesellschaften darunter — und alle Monatsspalten bleiben stehen.
 */
export function BerichtNachPerioden({
  bericht,
  verkehrszahl,
  kumuliert = false,
  aufriss,
  quelle,
  treffer,
  aufNachweis,
  aufEigenschaften,
  aufReihenfolge,
  aufBuchen,
  aufAnlegen,
}: {
  bericht: Periodenbericht
  /**
   * Ob die GuV *Kumuliert seit Jahresbeginn* zeigt — dann trägt jede Spalte
   * schon den Stand, und eine Zwischensumme wiederholte nur die letzte (R1018).
   */
  kumuliert?: boolean
  /**
   * **Verkehrszahl** statt Saldo (R426): die Veränderung zur Vorspalte statt
   * des Bestands. Gilt nur in der Bilanz — die GuV wählt zwischen Monatswert
   * und YTD, und das rechnet der Dienst, nicht die Anzeige.
   */
  verkehrszahl: boolean
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Periodenaufriss>
  treffer?: Trefferzustand
  /**
   * Doppelklick auf eine Zahl öffnet ihren Wertenachweis (R204).
   *
   * **Auf einer Verkehrszahl nicht:** Dort steht die Veränderung zur
   * Vorspalte, und die ist eine Anzeige, kein Ergebniswert. Ein Nachweis darauf
   * wiese eine Zahl nach, die im Modell nicht steht. Ein YTD-Wert der GuV
   * dagegen ist gerechnet und trägt seinen Nachweis.
   */
  aufNachweis?: Nachweisruf
  /** Rechtsklick → Buchen (R385). */
  aufBuchen?: Nachweisruf
  /** Rechtsklick → Anlegen: die fünfte Gruppe (R494, R500 — ein Schalter je Bericht). */
  aufAnlegen?: (art: 'konto' | 'position', position: string, positionsname: string) => void
  /** Rechtsklick → Eigenschaften (R858). */
  aufEigenschaften?: (ziel: Eigenschaftsziel) => void
  /** Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341). */
  aufReihenfolge?: (ziel: Reihenfolgeziel) => void
}) {
  const { menue, setMenue } = aufriss

  // Das Periodenbild hat seine eigenen Breiten (7.3, Abschnitt 10).
  const { breiten, rahmen } = useSpaltenbreiten(`${bericht.art}:perioden`)

  /*
    **Eine umgehängte Position steht zweimal da** (7.3 Abschnitt 33, R1723):
    an jeder Stelle, an der sie im Zeitraum hing. Damit beide Zeilen nicht
    gemeinsam auf- und zuklappen, heißt jede Zeile hier nach ihrer Kennung
    (`Position~Stelle`); wer einen Dienst ruft, nimmt die Position dahinter
    (`positionAus`). Im Regelfall ist die Kennung die Position selbst.
  */
  const ansicht = useMemo(
    () => ({ ...bericht, zeilen: bericht.zeilen.map((z) => ({ ...z, position: z.kennung ?? z.position })) }),
    [bericht],
  )

  // **Zwischensummen** (7.3 Abschnitt 26, R1018) und Nullzeilen — vor dem
  // frühen Ausstieg, weil ein Haken keine Bedingung verträgt.
  const optionen = useAnzeigeoptionen()

  // Bei der Verkehrszahl entfällt die erste Spalte: Es gibt keine Vorspalte,
  // gegen die sich etwas verändert haben könnte. **Mit einer einzigen Spalte
  // gibt es sie überhaupt nicht** — dann steht der Saldo, und das Feld in der
  // Kontextzeile steht nicht (R293, R528).
  const bewegt = verkehrszahl && bericht.spalten.length > 1

  /*
    **Leere Posten stehen da, bis einer sie wegnimmt** (7.3 Abschnitt 11,
    R420 — Die Nullzeilenunterdrückung gilt je Dimension) — auch im
    Periodenbild. Bis zum 2026-10-16 fehlte der Haken hier als einzigem Bild:
    Er stand über der Tabelle und tat nichts.

    **Leer ist, was in keiner Spalte einen Betrag zeigt** — gemessen an den
    Zahlen, die dort stehen. Auf Verkehrszahl ist das die Veränderung: Ein
    Posten, dessen Bestand im ganzen Zeitraum gleich bleibt, zeigt in jeder
    Spalte null und geht mit weg.
  */
  const vollstaendig = useMemo(() => {
    if (!unterdrueckt(optionen, 'positionen')) {
      return ansicht.zeilen
    }
    const leer = { einheit: null, konto: null }
    return ohneLeereZeilen(ansicht.zeilen, (z) =>
      alleNull(reihe(z.werte, bericht.spalten, bewegt, leer, z.beitraege)),
    )
  }, [ansicht, optionen, bericht.spalten, bewegt])

  // **Die Gliederung gilt in allen Spaltenbildern** (R446): Sie betrifft die
  // Zeilenachse, und die ist hier dieselbe wie im Strukturbild. Wer im
  // Periodenbild zuklappt und hinüberwechselt, findet dieselbe Gliederung vor.
  // Nach der Nullzeilenunterdrückung gerechnet, wie in den anderen Bildern.
  const gliederung = useGliederungsbild(vollstaendig)

  // **Eine Zeiteinheit ist ein Zeitraum** (R289, R528): Der Zeitschieber nennt
  // beide Enden, und wer sie auf dieselbe Zeiteinheit stellt, hat eine Spalte
  // bestellt — keinen Vergleich. Bis zum 2026-09-14 saß hier ein Riegel aus
  // dem ersten Bau der Spaltenbilder, der zwei Spalten verlangte; der Befund
  // des Auftraggebers: Der Bericht soll genau den Schieber zeigen, in Anzahl
  // und Raster. Leer ist nur, wo im Zeitraum keine Periode gerechnet ist.
  if (bericht.spalten.length === 0) {
    return <p className="hinweis">{t('spalten.perioden.leer')}</p>
  }

  const aufMenue: Menueruf = (x, y, position, eltern, zelle, kontext) =>
    setMenue({ x, y, position, eltern, zelle, kontext })

  const spalten = bewegt ? bericht.spalten.slice(1) : bericht.spalten

  // **Nur wo die Spalten Bewegungen zeigen**: Verkehrszahl in der Bilanz,
  // Monatswert in der GuV. Ein Saldo oder ein kumulierter Wert ist schon der
  // Stand; seine Zwischensumme wäre die letzte Spalte noch einmal.
  const summierbar = bericht.art === 'guv' ? !kumuliert : bewegt
  const perioden = spalten.map((s) => s.periode)
  const zwischen = summierbar ? zwischenspalten(perioden, optionen.zwischensummen ?? new Set()) : []
  const folge = spaltenfolge(spalten.length, zwischen)

  return (
    <div className="tabellenrahmen" ref={rahmen}>
      <table className="positionstabelle">
        <Spaltenleiste breiten={breiten} spalten={1 + folge.length} />
        <thead>
          <tr>
            {/* Derselbe Name wie im Strukturbild: derselbe Bericht, anders
                geschnitten (R411). */}
            <th className="positionsspalte">
              {t(bericht.art === 'guv' ? 'bereich.berichte.guv' : 'bereich.berichte.bilanz')}
            </th>
            {/*
                **Der Monat steht kurz** (Auftrag vom 2026-09-26): Zwoelf Spalten
                nebeneinander, und "September 2026" ist fast so breit wie die
                Zahl darunter. Das Jahr bleibt -- ein Bericht ueber zwei
                Geschaeftsjahre haette sonst zwei Spalten "Jan.".
            */}
            {folge.map((platz) => {
              if (platz.art === 'zwischen') {
                return (
                  <th key={`z-${platz.z.art}-${platz.z.bis}`} className="zahl spaltensumme zwischensumme">
                    {t('zwischensumme.kopf')}
                    <span className="kopfvermerk">{zwischenkopf(perioden, platz.z)}</span>
                  </th>
                )
              }
              const s = spalten[platz.i]!
              // Kein Vermerk zur Fortschreibung im Monatskopf (2.12
              // Abschnitt 3.20, R975 — seit dem 2026-10-06 weggelassen).
              return (
                <th key={s.periode} className="zahl">
                  {periodenkuerzel(s.periode)}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {gliederung.zeilen.map((zeile) => {
            const offen = aufriss.zustand.get(ebenenschluessel(zeile.position, []))
            const vorgabe = vorgabeFuer(zeile.beitraege)
            const moeglich = zeilenachsen(zeile, moeglicheAchsen(zeile.beitraege, [], zusatzachsen(quelle)))
            const aufreissbar = istAufreissbar(zeile, moeglich)
            const id = zeilenId(zeile.position, [])
            const leer: Zelle = { einheit: null, konto: null }
            const name = zeilenname(zeile)

            const werte = (z: Zelle, kennung: string, zahlen: number[]) => {
              const serie = reihe(zahlen, bericht.spalten, bewegt, z, zeile.beitraege)
              return (
                <Zellen ruf={aufNachweis ?? null} position={positionAus(zeile.position)} positionsname={name} zelle={z}>
                  {folge.map((platz) => {
                    // Eine Zwischensumme trägt keinen Nachweis: Sie steht in
                    // keiner Zelle des Modells, sie fasst Spalten zusammen.
                    if (platz.art === 'zwischen') {
                      return (
                        <Wert
                          key={`z-${platz.z.art}-${platz.z.bis}`}
                          wert={verdichte(serie, platz.z)}
                          klasse="spaltensumme zwischensumme"
                        />
                      )
                    }
                    const i = platz.i
                    return (
                      <Wert
                        key={spalten[i]?.periode ?? i}
                        wert={serie[i] ?? 0}
                        spalte={bewegt ? undefined : 'konzern'}
                        periode={spalten[i]?.periode}
                        fund={istTrefferzelle(treffer, kennung, periodenspalte(i))}
                      />
                    )
                  })}
                </Zellen>
              )
            }

            return (
              <Fragment key={zeile.position}>
                <tr
                  id={id}
                  className={[
                    rangklasse(zeile),
                    aufreissbar ? 'aufklappbar' : '',
                    // Keine Dämpfung für eine Zeile ohne Betrag (R867, 7.3 Abschnitt 21):
                    // Das leere Wertfeld sagt es, und jede Zeile steht in der Farbe ihrer Ebene.
                    trefferklasse(treffer, id),
                  ]
                    .join(' ')
                    .trim()}
                  tabIndex={0}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    const ort = menueort(e, e.currentTarget)
                    aufMenue(ort.x, ort.y, zeile.position, [], leer, zellkontext(e))
                  }}
                >
                  <td style={{ paddingLeft: einzug(zeile.ebene) }}>
                    <Zeilenkopf
                      aufreissbar={aufreissbar}
                      achse={offen}
                      vorgabe={vorgabe}
                      angebot={quelle}
                      aufKlappen={() =>
                        aufriss.umschalten(zeile.position, [], offen ?? moeglich[0] ?? vorgabe)
                      }
                      aufMenue={(x, y) => aufMenue(x, y, zeile.position, [], leer, {})}
                      klappbar={gliederung.klappbar(zeile.position)}
                      zugeklappt={gliederung.zugeklappt(zeile.position)}
                      aufGliederung={() => gliederung.umschalten(zeile.position)}
                    >
                      <Zeilenbeschriftung zeile={zeile} />
                    </Zeilenkopf>
                  </td>
                  {werte(leer, id, zeile.werte)}
                </tr>

                {/*
                  **Nur unter einer Zeile, die aufgerissen werden darf** (R165, R432).
                  Pfeil und Menü führen nicht auf eine Summenzeile — aber der
                  Aufklappzustand kommt auch von woanders: aus einem anderen Bericht,
                  aus der Adresse, und seit heute aus einem Suchtreffer. Ohne diesen
                  Riegel zeichnet die Tabelle, was kein Weg ihr hätte sagen dürfen.
                */}
                {aufreissbar && (
                  <Aufrisszeilen
                    position={zeile.position}
                    summe={zeile.istSumme}
                    ebeneTiefe={zeile.ebene}
                    zellen={zeile.beitraege}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={folge.length + 1}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    werteSpalten={(z, kennung) => werte(z, kennung, z.werte)}
                  />
                )}

                {/* Die eigenen Konten vor den Unterpositionen (7.3 Abschnitt 30, R1621). */}
                {!gliederung.zugeklappt(zeile.position) && eigeneZellen(zeile).length > 0 && (
                  <Aufrisszeilen
                    position={zeile.position}
                    fest="konten"
                    ebeneTiefe={zeile.ebene}
                    zellen={eigeneZellen(zeile)}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={folge.length + 1}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    werteSpalten={(z, kennung) => werte(z, kennung, z.werte)}
                  />
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={berichtsmenue({
            menue,
            aufEigenschaften,
            aufReihenfolge,
            bericht: ansicht,
            aufriss,
            quelle,
            // Auf einer Verkehrszahl gibt es keinen Nachweis: Die Zahl auf dem
            // Schirm steht in keiner Zelle des Modells.
            aufNachweis: bewegt ? undefined : aufNachweis,
            // Gebucht wird auch aus der Verkehrszahl heraus: Die Maske
            // erbt Periode und Bewertungsebene, und das Konto wählt der Anwender.
            aufAnlegen,
            aufBuchen,
            gliederung: gliederung.menue,
          })}
          aufSchliessen={() => setMenue(null)}
        />
      )}

      {/* **Kein Lauf und kein Prüfcode unter dem Bericht** (R868, 7.3 Abschnitt
          21): Hier stand je Monat „Lauf 431 · Prüfcode 375c56e1c5ed…". Sie
          stehen im Konzernabschluss-Monitor, im Nachweis und in der Prüferansicht. */}
      {bewegt && <p className="erlaeuterung">{t('bewegung.vorbehalt')}</p>}
    </div>
  )
}

/**
 * Die Gesellschaften, die im Strukturbild eine Spalte bekommen — **in der
 * Reihenfolge der Konzernstruktur** (7.3 Abschnitt 2c).
 *
 * Aus dem Bericht selbst und nicht aus den Stammdaten: Eine Gesellschaft ohne einen
 * einzigen Betrag bekommt keine leere Spalte. Die Suche braucht dieselbe
 * Liste, um zu wissen, welche Spalte sie getroffen hat.
 *
 * **Die Ordnung liefert der Bericht mit** (`struktur`): Mutter vor Tochter,
 * Geschwister nach Code, und zwar nach dem Beteiligungsbaum zum Stand des
 * Laufs. Alphabetisch standen die Töchter eines Teilkonzerns an sieben
 * verschiedenen Stellen der Tabelle — bei vier Gesellschaften unauffällig, bei
 * neunzehn der Unterschied zwischen einer Tabelle und einer Zettelsammlung.
 *
 * **Der Rückfall auf die alphabetische Reihenfolge bleibt**: Ein Bericht aus
 * der Zeit davor trägt kein `struktur`, und eine Tabelle ohne Spalten wäre die
 * schlechtere Antwort als eine in der falschen Ordnung.
 */
/**
 * Eine Spalte des Strukturbilds (7.3 Abschnitt 9, R407–R409).
 *
 * | Art | Was darin steht |
 * |---|---|
 * | `achse` | Eine Gesellschaft (oder ein Partner): ihre Handelsbilanz II |
 * | `stufe` | Ein **zugeklappter** Teilkonzern: seine konsolidierte Gesamtsumme |
 * | `stufensumme` | Aufgeklappt: die Summe seiner Gesellschaften |
 * | `stufenkonso` | Aufgeklappt: was **in ihm** eliminiert wird |
 * | `stufengesamt` | Aufgeklappt: beides zusammen — dieselbe Zahl wie zugeklappt |
 *
 * `block` nennt den aufgeklappten Teilkonzern, in dem die Spalte steht — daran
 * hängen der graue Grund und die Überschrift darüber (R408).
 */
export type Strukturspalte = {
  art: 'achse' | 'stufe' | 'stufensumme' | 'stufenkonso' | 'stufengesamt'
  kennung: string | null
  block?: string
}

/**
 * Die Spalten des Strukturbilds, in der Reihenfolge der Konzernstruktur.
 *
 * **Ein Teilkonzern steht zugeklappt als eine Spalte** (R407). Aufgeklappt
 * steht dort seine Mutter, dann seine Gesellschaften, dann *Summe ·
 * Konsolidierung · Gesamt* — der ganze Bericht im Kleinen (R408).
 *
 * **Der äußerste zugeklappte Teilkonzern gewinnt.** Ist Frankreich zu, ist
 * alles darunter zu, gleich was jemand vorher aufgeklappt hatte — sonst
 * stünden Spalten da, deren Dach fehlt.
 */
export function strukturspalten(bericht: Berichtssatz, offen: Set<string>): Strukturspalte[] {
  const stufen = bericht.stufen ?? []
  const vorhanden = einheitenIm(bericht)

  if (stufen.length === 0) {
    return vorhanden.map((e) => ({ art: 'achse', kennung: e }))
  }

  // Von aussen nach innen: die Stufe mit den meisten Mitgliedern umfasst die
  // anderen. So findet "die aeusserste zugeklappte" den richtigen Deckel.
  const vonAussen = [...stufen].sort((a, b) => b.mitglieder.length - a.mitglieder.length)

  /** Der äußerste zugeklappte Teilkonzern über einer Gesellschaft. */
  const deckel = (einheit: string) =>
    vonAussen.find((s) => !offen.has(s.schluessel) && s.mitglieder.includes(einheit)) ?? null

  /** Die aufgeklappten Teilkonzerne, in denen eine Gesellschaft liegt — von außen nach innen. */
  const bloecke = (einheit: string) =>
    vonAussen.filter((s) => offen.has(s.schluessel) && s.mitglieder.includes(einheit))

  const saeulen: Strukturspalte[] = []
  const gezeigt = new Set<string>()

  for (const einheit of vorhanden) {
    const zu = deckel(einheit)

    if (zu) {
      // Zugeklappt: eine Spalte, und zwar nur beim ersten Mitglied.
      if (!gezeigt.has(zu.schluessel)) {
        gezeigt.add(zu.schluessel)
        saeulen.push({ art: 'stufe', kennung: zu.schluessel })
      }

      continue
    }

    const innen = bloecke(einheit)
    saeulen.push({ art: 'achse', kennung: einheit, block: innen[innen.length - 1]?.schluessel })

    // Wo ein aufgeklappter Teilkonzern endet, stehen seine drei Spalten --
    // innen zuerst, damit ein geschachtelter Block innerhalb des aeusseren
    // abschliesst und nicht umgekehrt.
    for (const s of [...innen].reverse()) {
      const letzte = s.mitglieder[s.mitglieder.length - 1]
      if (letzte !== einheit) {
        continue
      }

      saeulen.push({ art: 'stufensumme', kennung: s.schluessel, block: s.schluessel })
      saeulen.push({ art: 'stufenkonso', kennung: s.schluessel, block: s.schluessel })
      saeulen.push({ art: 'stufengesamt', kennung: s.schluessel, block: s.schluessel })
    }
  }

  return saeulen
}

/** Die Kennung einer Spalte — eindeutig auch dort, wo drei auf dieselbe Stufe zeigen. */
export function saeulenkennung(s: Strukturspalte): string {
  return `${s.art}:${s.kennung ?? '∅'}`
}

/**
 * Die Klassen einer Spalte: der graue Grund des Blocks und, wo es eine ist,
 * die Rolle im Teilkonzern (R408).
 */
export function saeulenklasse(s: Strukturspalte, dazu = ''): string {
  return [
    dazu,
    s.block ? 'imblock' : '',
    s.art === 'stufe' || s.art === 'stufengesamt' ? 'stufenwert' : '',
    s.art === 'stufensumme' ? 'spaltensumme' : '',
  ]
    .join(' ')
    .trim()
}

/**
 * Die Spalten in Blöcken: was zu einem aufgeklappten Teilkonzern gehört und
 * was zwischen den Blöcken steht (R408).
 *
 * `stufe: null` ist die Strecke dazwischen — Gesellschaften, die in keinem
 * aufgeklappten Teilkonzern liegen.
 */
export function blockleiste(
  spalten: Strukturspalte[],
): { stufe: string | null; spalten: Strukturspalte[] }[] {
  const leiste: { stufe: string | null; spalten: Strukturspalte[] }[] = []

  for (const s of spalten) {
    const hier = s.block ?? null
    const letzte = leiste[leiste.length - 1]

    if (letzte && letzte.stufe === hier) {
      letzte.spalten.push(s)
    } else {
      leiste.push({ stufe: hier, spalten: [s] })
    }
  }

  return leiste
}

export function einheitenIm(bericht: Berichtssatz): string[] {
  const vorhanden = new Set(
    bericht.zeilen
      .flatMap((z) => z.beitraege)
      .map((b) => b.einheit)
      .filter(Boolean) as string[],
  )

  return nachStruktur(vorhanden, bericht.struktur)
}

/**
 * Bringt die vorhandenen Gesellschaften in die mitgelieferte Ordnung.
 *
 * Was die Ordnung nicht kennt, folgt hinten nach Kennung — kein Fehlerfall,
 * sondern der Rand: eine Gesellschaft, deren Beteiligung in dieser Periode
 * nicht gilt und die trotzdem einen Betrag beiträgt. Sie fallen zu lassen
 * hieße, eine Spalte zu unterschlagen, in der eine Zahl steht.
 */
export function nachStruktur(vorhanden: Set<string>, ordnung: string[] | undefined): string[] {
  const gereiht = (ordnung ?? []).filter((e) => vorhanden.has(e))
  const bekannt = new Set(gereiht)

  return [...gereiht, ...[...vorhanden].filter((e) => !bekannt.has(e)).sort()]
}

/**
 * Die Partner, die im Partnerbild eine Spalte bekommen — aus dem Abruf, in
 * fester Reihenfolge, „Default" zuletzt: Es ist der Rest, nicht der
 * erste Wert.
 *
 * **Die Reihenfolge ist die der Partnercodes** (R493, seit dem 2026-09-13)
 * und nicht mehr die des Schlüssels. Der Anwender hat sie im
 * Stammdatenbereich selbst gesetzt; eine Liste, die ihn dort ordnen lässt und
 * ihn hier alphabetisch überfährt, hätte die Ordnung zweimal. Ein Partner
 * ohne gepflegten Code steht hinten und unter seinesgleichen alphabetisch.
 */
export function partnerIn(zellen: { wert: string | null }[]): (string | null)[] {
  const codes = katalog().partner
  const rang = (w: string) => codes.get(w)?.rang ?? Number.MAX_SAFE_INTEGER

  const mit = [...new Set(zellen.map((z) => z.wert).filter((w): w is string => w !== null))].sort(
    (a, b) => rang(a) - rang(b) || a.localeCompare(b, 'de'),
  )
  const ohne = zellen.some((z) => z.wert === null)
  return ohne ? [...mit, null] : mit
}

/**
 * Die Werte einer Zeile, je nach gewähltem Zeitwert (R426).
 *
 * **Die Verkehrszahl ist mehr als eine Subtraktion**, und heute noch
 * weniger, als sie sein soll. Der Unterschied zweier Stichtagsbestände enthält
 * drei Schichten:
 *
 *  1. die **eigentliche Bewegung** — was gekauft, verbraucht, bezahlt wurde,
 *  2. die **Kreisänderung** — eine Gesellschaft kommt hinzu und bringt ihren
 *     ganzen Bestand mit, ohne dass jemand etwas bewegt hätte,
 *  3. das **Währungsdelta** — derselbe Bestand in Landeswährung, ein anderer
 *     Stichtagskurs.
 *
 * Nur die erste Schicht gehört später in die Kapitalflussrechnung. Schicht 2
 * lässt sich heute abziehen, weil jede Periodenspalte ihren Kreisumfang
 * mitführt. **Schicht 3 nicht** — dafür fehlt die Bewegungsart in der
 * Ergebniszeile. Solange sie fehlt, steckt der Währungseffekt in der Bewegung,
 * und die Ansicht sagt das unter der Tabelle, statt es zu verschweigen.
 */
export function reihe(
  werte: number[],
  spalten: Periodenspalte[],
  verkehrszahl: boolean,
  zelle: Zelle,
  beitraege: Periodenaufriss[],
): number[] {
  // Eine einzige Spalte hat keine Vorspalte: Die Verkehrszahl ist dann nicht
  // zu bilden, und es steht der Saldo (R528) — nicht eine leere Reihe.
  if (!verkehrszahl || werte.length < 2) {
    return werte
  }

  const ergebnis: number[] = []
  for (let i = 1; i < werte.length; i++) {
    const zugang = kreiszugang(spalten[i - 1], spalten[i])
    ergebnis.push((werte[i] ?? 0) - (werte[i - 1] ?? 0) - anteilDerNeuen(zugang, i, zelle, beitraege))
  }

  return ergebnis
}

/** Welche Gesellschaften zwischen zwei Spalten hinzugekommen sind. */
function kreiszugang(vorher: Periodenspalte | undefined, nachher: Periodenspalte | undefined): string[] {
  if (!vorher || !nachher) {
    return []
  }

  const alt = new Set(vorher.einheiten)
  return nachher.einheiten.filter((e) => !alt.has(e))
}

/**
 * Was die neu hinzugekommenen Gesellschaften an dieser Stelle mitgebracht
 * haben — der Betrag, der aus der Bewegung herausgehört.
 *
 * Für eine Gesellschaftszeile ist die Rechnung von selbst richtig: Wer vorher
 * nicht im Kreis war, hatte dort keinen Wert, und die Differenz ist ohnehin
 * ihr ganzer Bestand. Für die Positions- und die Kontozeile aber nicht — dort
 * steckt der Zugang in einer Summe, aus der er einzeln herausgerechnet werden
 * muss.
 */
function anteilDerNeuen(
  zugang: string[],
  spalte: number,
  zelle: Zelle,
  beitraege: Periodenaufriss[],
): number {
  if (zugang.length === 0 || zelle.einheit !== null) {
    return 0
  }

  return beitraege
    .filter((b) => b.einheit !== null && zugang.includes(b.einheit) && b.konto === zelle.konto)
    .reduce((summe, b) => summe + (b.werte[spalte] ?? 0), 0)
}
